import { FormControl, MenuItem, Select, Stack } from '@mui/material';
import Button from '@mui/material/Button';
import axios from 'axios';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { handleUnauthorizedResponse } from '../../api/http';
import {
	AutofillOperations,
	AutofillOperationTypes,
} from '../../api/IntegrationsApi';
import PolicyApi from '../../api/PolicyApi';
import ProductApi from '../../api/ProductApi';
import {
	AuthContext,
	AuthData,
	MULTI_CURRENCY_FEATURE_ENABLE,
	UI_MULTI_CURRENCY_FEATURE_ENABLE,
} from '../../context/auth';
import { ConfigContext, ConfigData } from '../../context/config';
import { PolicyContext, PolicyData } from '../../context/policy';
import currencyCodes from '../../data/currencyCodes';
import {
	NEW_APPLICATION_BILLING_TYPE,
	NEW_APPLICATION_CURRENCY_TYPE,
	NEW_APPLICATION_FAIL_TOAST,
	NEW_APPLICATION_PRODUCT_TYPE,
} from '../../data/labelsAndMessages';
import { getReadableName, isAutofillConfigured } from '../../util/util';
import FieldLabel from '../fields/FieldLabel';
import LargeCenterSpinner from '../LargeCenterSpinner';
import PolicyholderSearch from '../policyholder/PolicyholderSearch';
import { errorToast } from '../Toast';

interface Props {
	authData: AuthData;
	configData: ConfigData;
	policyData: PolicyData;
}

function NewApplicationView(props: Props) {
	const navigate = useNavigate();
	const { authData, configData, policyData } = props;
	const multiCurrencyEnable =
		authData?.auth?.configProperties?.[MULTI_CURRENCY_FEATURE_ENABLE] &&
		authData?.auth?.configProperties?.[UI_MULTI_CURRENCY_FEATURE_ENABLE];
	const [loading, setLoading] = useState(false);
	const [products, setProducts] = useState([]);
	const [product, setProduct] = useState('');
	const [billingTypes, setBillingTypes] = useState([]);
	const [billingType, setBillingType] = useState('');
	const [policyholder, setPolicyholder] = useState('');
	const { tenantConfiguration } = configData;
	const [currency, setCurrency] = useState(
		tenantConfiguration ? tenantConfiguration.currencyCode : '',
	);

	useEffect(() => {
		if (tenantConfiguration && tenantConfiguration.currencyCode) {
			setCurrency(tenantConfiguration.currencyCode);
		}
	}, [tenantConfiguration]);
	useEffect(() => {
		const source = axios.CancelToken.source();
		// get list of products upon first mounting
		ProductApi.getActiveProducts(
			authData.auth.authorizationToken,
			authData.auth.tenantId,
			{ cancelToken: source.token },
		)
			.then((productsArr) => {
				const productNames = productsArr.data.reduce(
					(filtered: any, prod: any) => {
						// filter out the 'global' product
						if (prod.name !== 'global' && prod.enabled) {
							filtered.push(prod.name);
						}
						return filtered;
					},
					[],
				);
				const formattedProductNames = productNames.map((p: string) => {
					return { value: p, label: getReadableName(p) };
				});
				// set products in state
				setProducts(formattedProductNames);
			})
			.catch(async (error) => {
				if (axios.isCancel(error)) {
					return;
				} else {
					if (await handleUnauthorizedResponse(error, authData)) {
						return;
					}
				}
			});

		return () => {
			source.cancel();
		};
	}, []);

	if (loading) {
		return <LargeCenterSpinner />;
	}

	const updateProduct = (event: ChangeEvent<HTMLInputElement>) => {
		const val = event.target.value;
		setProduct(val);
		if (!val) {
			setBillingTypes([]);
			return;
		}
		const productConfig = configData.productConfigs.find(
			(prodConfigs) => prodConfigs.name === val,
		);
		const premiumReportingConfig = productConfig.premiumReportingConfiguration;
		if (premiumReportingConfig) {
			const billingOptions = premiumReportingConfig.map((pr) => {
				return { value: pr.reportName, label: pr.reportName };
			});
			setBillingTypes(billingOptions);
		} else {
			setBillingTypes([]); // reset billing types if product does not have premium reporting configured
		}
	};

	const updateBillingType = (event: ChangeEvent<HTMLInputElement>) => {
		setBillingType(event.target.value);
	};

	const updatePolicyholder = (val: string) => {
		setPolicyholder(val);
	};

	const updateCurrency = (event: ChangeEvent<HTMLInputElement>) => {
		setCurrency(event.target.value);
	};

	const createApplication = async () => {
		const autofillEnabled = isAutofillConfigured(
			product,
			AutofillOperations.NEW_BUSINESS,
			AutofillOperationTypes.CREATE,
			configData?.dataAutofillSequences ?? [],
		);

		const req: any = {
			finalize: false,
			productName: product,
			policyholderLocator: policyholder,
			currency,
			// default specified autofill sequence will run when autofill array is null
			autofill: autofillEnabled ? null : [],
		};
		if (billingType) {
			req.premiumReportName = billingType;
		}

		try {
			setLoading(true);
			const policy = (
				await PolicyApi.createPolicy(authData.auth.authorizationToken, req)
			).data;
			await policyData.loadData(policy.locator);
			// reset update object
			await policyData.setPolicyUpdateObj({});
			await policyData.setQuoteUpdateObj({});
			// redirect to draft policy page
			setLoading(false);
			navigate(`/policy/${policy.locator}`);
		} catch (error) {
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
			errorToast(NEW_APPLICATION_FAIL_TOAST());
			setLoading(false);
		}
	};

	let canConfirm = product && policyholder;
	if (billingTypes.length && multiCurrencyEnable) {
		canConfirm = canConfirm && billingType && currency;
	} else if (billingTypes.length) {
		canConfirm = canConfirm && billingType;
	} else if (multiCurrencyEnable) {
		canConfirm = canConfirm && currency;
	}

	const buttonAction = canConfirm ? createApplication : null;

	const productDropdown = (
		<FormControl variant='outlined' fullWidth>
			<FieldLabel title={NEW_APPLICATION_PRODUCT_TYPE()} required />
			<Select
				variant='outlined'
				title='Product'
				id='product-select'
				value={product}
				required
				onChange={updateProduct}
			>
				{products.map((productDisplayed) => (
					<MenuItem key={productDisplayed.label} value={productDisplayed.value}>
						{productDisplayed.label}
					</MenuItem>
				))}
			</Select>
		</FormControl>
	);
	const currencyDropdown = (
		<FormControl variant='outlined' fullWidth>
			<FieldLabel title={NEW_APPLICATION_CURRENCY_TYPE()} required />
			<Select
				title='Currency'
				variant='outlined'
				id='currency-select'
				value={currency}
				required
				onChange={updateCurrency}
			>
				{currencyCodes.map((code) => (
					<MenuItem key={code.label} value={code.value}>
						{code.label}
					</MenuItem>
				))}
			</Select>
		</FormControl>
	);
	const reportName = (
		<FormControl variant='outlined' fullWidth>
			<FieldLabel title={NEW_APPLICATION_BILLING_TYPE()} required />
			<Select
				variant='outlined'
				title='Billing Type'
				id='billing-type-select'
				value={billingType}
				required={!!billingTypes.length}
				disabled={!billingTypes.length}
				onChange={updateBillingType}
			>
				{billingTypes.map((type) => (
					<MenuItem key={type.label} value={type.value}>
						{type.label}
					</MenuItem>
				))}
			</Select>
		</FormControl>
	);
	const policyholderId = <PolicyholderSearch update={updatePolicyholder} />;
	return (
		<Stack spacing={2}>
			{productDropdown}
			{multiCurrencyEnable ? currencyDropdown : null}
			{billingTypes.length ? reportName : null}
			{policyholderId}
			<Stack spacing={2} direction='row' justifyContent='flex-end'>
				<Button onClick={() => navigate(-1)}>Cancel</Button>
				<Button color='primary' variant='contained' onClick={buttonAction}>
					Create
				</Button>
			</Stack>
		</Stack>
	);
}

function withContext(Component: any) {
	return (
		props: Omit<
			Props,
			'configData' | 'authData' | 'policyholderData' | 'policyData'
		>,
	) => (
		<ConfigContext.Consumer>
			{(configData) => (
				<AuthContext.Consumer>
					{(authData) => (
						<PolicyContext.Consumer>
							{(policyData) => (
								<Component
									{...props}
									configData={configData}
									authData={authData}
									policyData={policyData}
								/>
							)}
						</PolicyContext.Consumer>
					)}
				</AuthContext.Consumer>
			)}
		</ConfigContext.Consumer>
	);
}

export default withContext(NewApplicationView);
